import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";

const STATUS_LABELS = {
  analyzing: { label: "Analisi", color: "bg-slate-100 text-slate-700" },
  awaiting_info: { label: "Attesa info", color: "bg-amber-50 text-amber-800 border-amber-200" },
  matching: { label: "Matching", color: "bg-blue-50 text-blue-800 border-blue-200" },
  completed: { label: "Completata", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function AdminSessions() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [status, setStatus] = useState("");
  const [operator, setOperator] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const r = await api.sessions({ page, status, operator });
      setData(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="space-y-5" data-testid="admin-sessions">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
          Sessioni
        </h1>
        <p className="text-sm text-slate-500">
          Storico richieste · totale{" "}
          <span className="font-mono">{data.total}</span>
        </p>
      </header>

      <div className="flex items-center gap-3">
        <Select
          value={status || "ALL"}
          onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}
        >
          <SelectTrigger
            className="w-44 rounded-sm border-slate-200"
            data-testid="sessions-filter-status"
          >
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(1)}
          placeholder="Operatore..."
          className="w-60 rounded-sm border-slate-200 font-mono text-sm"
          data-testid="sessions-filter-operator"
        />
        <Button
          variant="outline"
          onClick={() => load(1)}
          className="rounded-sm border-slate-300"
        >
          Cerca
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 w-44">
                Data
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Cliente
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 w-24">
                Voci
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 w-36">
                Operatore
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 w-36">
                Stato
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                ID sessione
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                  Caricamento...
                </TableCell>
              </TableRow>
            ) : data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                  Nessuna sessione registrata.
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((s) => {
                const meta = STATUS_LABELS[s.status] || STATUS_LABELS.analyzing;
                return (
                  <TableRow key={s.session_id} data-testid={`sess-${s.session_id}`}>
                    <TableCell className="font-mono text-xs">
                      {new Date(s.created_at).toLocaleString("it-IT")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.cliente_nome || "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.voci_count}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.operator_name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`${meta.color} border rounded-sm font-mono text-[10px] hover:bg-current/0`}
                      >
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-500">
                      {s.session_id.slice(0, 8)}...
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
